import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { PracticeSession, type Mode, type NoteResult, type NoteState, type SessionEvent, type SessionStatus, type SessionSummary } from '../../engine/session';
import { inputManager } from '../../input/manager';
import { findScore } from '../../music/catalog';
import { measureCount, type Hand, type Score } from '../../music/model';
import { noteName, type NamingSystem } from '../../music/pitchNames';
import { defaultHands, handsOverlap, handsWarning, isMonophonic } from '../../setup/diagnostics';
import { recordSessionEnd, recordSureHit, type Award } from '../../store/missions';
import { saveSession } from '../../store/progress';
import { settingsStore, TOLERANCE_MS, WIDE_WINDOW_MS, type DisplayMode } from '../../store/settings';
import { InputPicker } from '../components/InputPicker';
import { RewardToast } from '../components/RewardToast';
import { SheetView, type SheetHandle } from '../components/SheetView';
import { SummaryPanel } from '../components/SummaryPanel';
import { handLabel } from '../format';
import { useInputStatus, useSettings } from '../hooks/stores';
import type { InputKind } from '../../engine/types';
import { activateInput, INPUT_LABELS } from '../inputControl';
import { Metronome } from '../metronome';
import { computeLayout, drawStage, pitchAtPoint, type KeyFlash, type StageLayout } from '../render/stage';
import { href } from '../router';

const COLORS: Record<NoteResult['category'], string> = {
  correct: '#1f9d5f',
  early: '#d98a0b',
  late: '#d98a0b',
  wrong: '#e5484d',
  wrongOctave: '#e5484d',
  extra: '#e5484d',
  missed: '#e5484d',
  uncertain: '#7e8a97',
};

const DISPLAY_LABELS: Record<DisplayMode, string> = { falling: 'Notes', sheet: 'Partition', both: 'Les deux' };

function describeResult(r: NoteResult, naming: NamingSystem): string {
  const name = (p: number | null | undefined) => (p === null || p === undefined ? '?' : noteName(p, naming));
  const delta = r.deltaMs !== undefined ? ` (${r.deltaMs > 0 ? '+' : ''}${r.deltaMs} ms)` : '';
  const prefix = r.revised ? 'Rattrapée. ' : '';
  switch (r.category) {
    case 'correct':
      return `${prefix}Juste : ${name(r.pitch)}${delta}`;
    case 'early':
      return `${prefix}En avance : ${name(r.pitch)}${delta}`;
    case 'late':
      return `${prefix}En retard : ${name(r.pitch)}${delta}`;
    case 'wrong':
      return `Fausse note : ${name(r.pitch)} au lieu de ${name(r.expectedPitch)}`;
    case 'wrongOctave': {
      const dir = r.pitch !== null && r.expectedPitch !== undefined && r.pitch > r.expectedPitch ? 'trop haut' : 'trop bas';
      return `Bonne note, mauvaise octave : ${name(r.pitch)} une octave ${dir}`;
    }
    case 'extra':
      return `Note en trop : ${name(r.pitch)}`;
    case 'missed':
      return `Note manquée : ${name(r.expectedPitch)}`;
    case 'uncertain':
      return r.pitch === null ? 'Détection incertaine (attaque sans hauteur)' : `Détection incertaine : ${name(r.pitch)} ?`;
  }
}

interface Toast {
  id: number;
  text: string;
  color: string;
  until: number;
}

function suggestedTempo(score: Score): number {
  // Au moins 80 % du tempo cible, seuil de validation, arrondi au multiple de 5 supérieur.
  return Math.max(score.minTempo, Math.ceil((score.tempo * 0.8) / 5) * 5);
}

export function PracticePage({ scoreId, flags = [] }: { scoreId: string; flags?: readonly string[] }) {
  const score = findScore(scoreId);
  if (!score) {
    return (
      <main className="page">
        <p>Morceau introuvable.</p>
        <a className="btn" href={href('catalogue')}>
          Retour au catalogue
        </a>
      </main>
    );
  }
  return <Practice key={score.id} score={score} flags={flags} />;
}

function Practice({ score, flags }: { score: Score; flags: readonly string[] }) {
  const settings = useSettings();
  const ensureFalling = flags.includes('notes');
  // Accès « Jouer un morceau » : les notes descendantes doivent être visibles, quel que soit le réglage.
  useEffect(() => {
    if (ensureFalling && settingsStore.get().display === 'sheet') settingsStore.set({ display: 'both' });
  }, [ensureFalling]);
  const inputStatus = useInputStatus();
  const availableHands = useMemo(() => score.parts.map((p) => p.hand), [score]);
  const [mode, setMode] = useState<Mode>(flags.includes('tempo') ? 'tempo' : 'wait');
  // Mains à l'ouverture : main demandée par l'adresse (md, mg), sinon la règle commune à
  // l'assistant, l'accueil et le catalogue : au micro, une seule main quand les deux jouent ensemble.
  const [hands, setHands] = useState<readonly Hand[]>(() => {
    if (flags.includes('md') && availableHands.includes('right')) return ['right'];
    if (flags.includes('mg') && availableHands.includes('left')) return ['left'];
    return defaultHands(score, inputManager.currentKind ?? settingsStore.get().inputKind);
  });
  const [tempo, setTempo] = useState(() => suggestedTempo(score));
  const [status, setStatus] = useState<SessionStatus>('idle');
  const [beat, setBeat] = useState<{ beat: number; countIn: boolean; measure: number } | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [summary, setSummary] = useState<SessionSummary | null>(null);
  const [counts, setCounts] = useState({ hit: 0, total: 0 });
  const [pauseReason, setPauseReason] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [waitingSince, setWaitingSince] = useState<number | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [sheetStates, setSheetStates] = useState<ReadonlyMap<string, NoteState>>(() => new Map());
  const [expectedIds, setExpectedIds] = useState<readonly string[]>([]);
  // Missions : récompenses à afficher (animation) et récompenses obtenues pendant cette séance (bilan).
  const [rewards, setRewards] = useState<Award[]>([]);
  const [sessionAwards, setSessionAwards] = useState<Award[]>([]);
  const display = settings.display;

  const sessionRef = useRef<PracticeSession | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const layoutRef = useRef<StageLayout | null>(null);
  const flashesRef = useRef<KeyFlash[]>([]);
  const heldRef = useRef(new Map<number, string>());
  const noteStatesRef = useRef<Map<string, NoteState>>(new Map());
  const startedAtRef = useRef(0);
  const metronomeRef = useRef<Metronome | null>(null);
  const toastIdRef = useRef(0);
  const pointersRef = useRef(new Map<number, number>());
  const namingRef = useRef(settings.naming);
  namingRef.current = settings.naming;
  const sheetRef = useRef<SheetHandle>(null);
  const expectedKeyRef = useRef('');
  // Notes attendues acceptées par le moteur (justes, sûres) depuis le début de la séance, une seule
  // fois par identifiant de note ; sources vues ; arrêt manuel.
  const sureHitsRef = useRef(0);
  const countedNotesRef = useRef(new Set<string>());
  const sourcesRef = useRef(new Set<InputKind>());
  const stoppedRef = useRef(false);

  const toleranceMs = TOLERANCE_MS[settings.tolerance];
  // Les événements micro sont antidatés de la compensation de latence : une note attendue n'est
  // déclarée manquée qu'après ce délai, plus une marge de traitement.
  const inputDelayMs = Math.max(150, settings.micLatencyMs + 100);
  const activeKind = inputManager.currentKind;
  const listeningKind = activeKind ?? settings.inputKind;
  const handsNotice = handsWarning(score, hands, listeningKind);
  const oneHandNotice =
    !handsNotice && isMonophonic(listeningKind) && handsOverlap(score) && hands.length === 1
      ? `Au micro, une main à la fois : ${hands[0] === 'right' ? 'main droite' : 'main gauche'} sélectionnée. Changez avec MD / MG.`
      : null;
  const measures = measureCount(score);

  const createSession = useCallback(() => {
    const s = new PracticeSession(score, { mode, hands, tempo, toleranceMs, wideMs: WIDE_WINDOW_MS, inputDelayMs });
    sessionRef.current = s;
    noteStatesRef.current = s.noteStates();
    flashesRef.current = [];
    sureHitsRef.current = 0;
    countedNotesRef.current = new Set();
    sourcesRef.current = new Set();
    stoppedRef.current = false;
    setSessionAwards([]);
    setSheetStates(noteStatesRef.current);
    setExpectedIds([]);
    expectedKeyRef.current = '';
    setStatus('idle');
    setSummary(null);
    setCounts({ hit: 0, total: s.expectedCount });
    setBeat(null);
    setPauseReason(null);
    setWaitingSince(null);
    setHint(null);
    setToasts([]);
    return s;
  }, [score, mode, hands, tempo, toleranceMs, inputDelayMs]);

  useEffect(() => {
    createSession();
  }, [createSession]);

  useEffect(() => {
    const m = new Metronome();
    metronomeRef.current = m;
    return () => {
      m.dispose();
      metronomeRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (metronomeRef.current) metronomeRef.current.enabled = settings.audioClick;
  }, [settings.audioClick]);

  const pushToast = useCallback((text: string, color: string) => {
    const now = performance.now();
    setToasts((list) => [...list.filter((t) => t.until > now).slice(-2), { id: ++toastIdRef.current, text, color, until: now + 2200 }]);
  }, []);

  const handleEvents = useCallback(
    (events: SessionEvent[]) => {
      const s = sessionRef.current;
      if (!s || events.length === 0) return;
      const now = performance.now();
      for (const ev of events) {
        switch (ev.type) {
          case 'feedback': {
            const r = ev.result;
            const color = COLORS[r.category];
            pushToast(describeResult(r, namingRef.current), color);
            // Mission « cinq notes justes » : notes attendues acceptées par le moteur (identifiant de note
            // présent), à la bonne hauteur et reconnues avec certitude, comptées une seule fois chacune.
            // Un simple avertissement « en avance » du mode attente n'a pas d'identifiant : la note reste à
            // jouer et ne compte pas. Une note « rattrapée » garde l'identifiant déjà compté.
            const accepted = (r.category === 'correct' || r.category === 'early' || r.category === 'late') && r.noteId !== undefined;
            if (accepted && r.noteId && !countedNotesRef.current.has(r.noteId)) {
              countedNotesRef.current.add(r.noteId);
              sureHitsRef.current += 1;
              const got = recordSureHit({ sureHits: sureHitsRef.current, sources: [...sourcesRef.current], scoreId: score.id });
              if (got.length > 0) {
                setRewards((list) => [...list, ...got]);
                setSessionAwards((list) => [...list, ...got]);
              }
            }
            const flashPitch = r.pitch ?? r.expectedPitch;
            if (flashPitch !== undefined && flashPitch !== null) flashesRef.current.push({ pitch: flashPitch, color, until: now + 450 });
            noteStatesRef.current = s.noteStates();
            setSheetStates(noteStatesRef.current);
            setCounts({ hit: s.hitCount, total: s.expectedCount });
            break;
          }
          case 'beat':
            setBeat({ beat: ev.beat, countIn: ev.countIn, measure: ev.measure });
            metronomeRef.current?.click(ev.beat === 1);
            break;
          case 'status':
            setStatus(ev.status);
            setPauseReason(ev.status === 'paused' ? (ev.reason ?? 'connexion perdue') : null);
            setWaitingSince(ev.status === 'waiting' ? now : null);
            if (ev.status !== 'waiting') setHint(null);
            break;
          case 'step':
            break;
          case 'end': {
            setSummary(ev.summary);
            saveSession(score.id, ev.summary, score.tempo, now - startedAtRef.current);
            const got = recordSessionEnd({ score, summary: ev.summary, completed: !stoppedRef.current, targetTempo: score.tempo });
            if (got.length > 0) {
              setRewards((list) => [...list, ...got]);
              setSessionAwards((list) => [...list, ...got]);
            }
            break;
          }
        }
      }
    },
    [pushToast, score],
  );

  // Événements d'entrée : touches enfoncées pour l'affichage, puis appariement par la session.
  // La session est d'abord avancée à l'instant présent : si l'animation est figée (onglet
  // en arrière-plan), l'arrivée sur un pas est tout de même prise en compte avant la note.
  useEffect(() => {
    return inputManager.subscribe((e) => {
      if (e.type === 'noteOn') {
        heldRef.current.set(e.pitch, e.confidence === 'sure' ? '#14b8b0' : '#9aa5b1');
        sourcesRef.current.add(e.source);
      }
      if (e.type === 'noteOff') heldRef.current.delete(e.pitch);
      const s = sessionRef.current;
      if (!s) return;
      handleEvents(s.update(performance.now()));
      handleEvents(s.input(e));
    });
  }, [handleEvents]);

  // Minuterie de secours : fait avancer la session même quand requestAnimationFrame est suspendu.
  useEffect(() => {
    const id = window.setInterval(() => {
      const s = sessionRef.current;
      if (s) handleEvents(s.update(performance.now()));
    }, 250);
    return () => window.clearInterval(id);
  }, [handleEvents]);

  // Boucle d'animation : avance la session, dessine la scène et positionne le curseur de la partition.
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const now = performance.now();
      const s = sessionRef.current;
      const canvas = canvasRef.current;
      const layout = layoutRef.current;
      if (s) handleEvents(s.update(now));
      if (s) {
        const tick = s.tickAt(now);
        const waiting = s.status === 'waiting';
        const expectedPitches = waiting ? s.pendingPitches : [];
        if (canvas && layout) {
          const ctx = canvas.getContext('2d');
          if (ctx) {
            flashesRef.current = flashesRef.current.filter((f) => f.until > now);
            drawStage(ctx, layout, {
              score,
              hands,
              tick,
              now,
              noteStates: noteStatesRef.current,
              expectedPitches,
              heldPitches: heldRef.current,
              flashes: flashesRef.current,
              naming: settings.naming,
              showNames: settings.showNames,
              showFingers: settings.showFingers,
              waiting,
              showNotes: display !== 'sheet',
            });
          }
        }
        sheetRef.current?.setTick(tick);
        const step = waiting ? s.steps[s.currentStepIndex] : undefined;
        const ids = step ? step.notes.filter((n) => expectedPitches.includes(n.pitch)).map((n) => n.id) : [];
        const key = ids.join(',');
        if (key !== expectedKeyRef.current) {
          expectedKeyRef.current = key;
          setExpectedIds(ids);
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [score, hands, display, settings.naming, settings.showNames, settings.showFingers, handleEvents]);

  // Dimensions du canvas.
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      // La taille CSS du canvas suit son conteneur (100 %) : seule la mémoire de dessin est
      // dimensionnée ici, pour que la scène puisse rétrécir avec la fenêtre.
      const w = Math.max(200, Math.round(rect.width));
      const h = Math.max(60, Math.round(rect.height));
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      layoutRef.current = computeLayout(score, hands, w, h, { keyboardOnly: display === 'sheet' });
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [score, hands, display]);

  // Nettoyage des messages expirés.
  useEffect(() => {
    if (toasts.length === 0) return;
    const id = window.setInterval(() => {
      const now = performance.now();
      setToasts((list) => (list.some((t) => t.until <= now) ? list.filter((t) => t.until > now) : list));
    }, 250);
    return () => window.clearInterval(id);
  }, [toasts.length]);

  // Indice après quelques secondes d'attente.
  useEffect(() => {
    if (waitingSince === null) return;
    const id = window.setTimeout(() => {
      const s = sessionRef.current;
      if (!s || s.status !== 'waiting') return;
      const step = s.steps[s.currentStepIndex];
      const parts = s.pendingPitches.map((p) => {
        const note = step?.notes.find((n) => n.pitch === p);
        const finger = note?.finger && settings.showFingers ? `, doigt ${note.finger}` : '';
        const hand = note ? ` (${handLabel(note.hand, true)}${finger})` : '';
        return `${noteName(p, settings.naming)}${hand}`;
      });
      setHint(parts.length > 1 ? `Notes attendues : ${parts.join(' + ')}` : `Note attendue : ${parts[0] ?? ''}`);
    }, 4000);
    return () => window.clearTimeout(id);
  }, [waitingSince, settings.naming, settings.showFingers]);

  const running = status !== 'idle' && status !== 'ended';

  const start = async () => {
    setInputError(null);
    if (!inputManager.currentKind) {
      await activateInput();
      const st = inputManager.getStatus();
      if (st.state === 'error' || st.state === 'unsupported') {
        setInputError(st.message);
        setShowPicker(true);
        return;
      }
    }
    let s = sessionRef.current;
    if (!s || s.status !== 'idle') s = createSession();
    if (metronomeRef.current) metronomeRef.current.enabled = settings.audioClick;
    startedAtRef.current = performance.now();
    handleEvents(s.start(performance.now()));
  };

  const stop = () => {
    const s = sessionRef.current;
    if (!s) return;
    stoppedRef.current = true;
    handleEvents(s.stop());
  };

  const changeMode = (m: Mode) => {
    if (running) stop();
    setMode(m);
  };
  const changeHands = (h: readonly Hand[]) => {
    if (running) stop();
    setHands(h);
  };
  const changeTempo = (t: number) => {
    if (running) stop();
    setTempo(Math.max(score.minTempo, Math.min(Math.round(score.tempo * 1.2), t)));
  };
  // Changer d'affichage ne touche pas à la session : position et résultats sont conservés.
  const changeDisplay = (d: DisplayMode) => settingsStore.set({ display: d });

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const layout = layoutRef.current;
    if (!layout) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pitch = pitchAtPoint(layout, e.clientX - rect.left, e.clientY - rect.top);
    if (pitch === null) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, pitch);
    inputManager.keyboard.press(pitch);
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const pitch = pointersRef.current.get(e.pointerId);
    if (pitch === undefined) return;
    pointersRef.current.delete(e.pointerId);
    inputManager.keyboard.release(pitch);
  };

  const handOptions: { label: string; short: string; value: readonly Hand[] }[] =
    availableHands.length > 1
      ? [
          { label: 'Main droite', short: 'MD', value: ['right'] },
          { label: 'Main gauche', short: 'MG', value: ['left'] },
          { label: 'Deux mains', short: 'MD + MG', value: availableHands },
        ]
      : [];
  const sameHands = (a: readonly Hand[], b: readonly Hand[]) => a.length === b.length && a.every((h) => b.includes(h));
  const beatsPerMeasure = score.timeSignature.beats;
  const currentMeasure = beat && !beat.countIn ? Math.min(measures, beat.measure + 1) : 0;
  const inputLabel = activeKind ? INPUT_LABELS[activeKind] : 'aucune source';
  // Instruction courante : dans l'affichage « Partition », elle occupe une bande réservée
  // entre la partition et le clavier ; ailleurs, elle est superposée aux notes descendantes.
  const instruction =
    status === 'idle' && !summary
      ? mode === 'wait'
        ? 'Mode attente : la lecture attend chacune de vos notes.'
        : `Mode tempo imposé : décompte d’une mesure, puis ça défile à ♩ = ${tempo}.`
      : status === 'leadIn' && mode === 'wait'
        ? 'Jouez chaque note quand elle atteint la ligne. La lecture attend votre note.'
        : status === 'waiting' && hint
          ? hint
          : status === 'ended'
            ? 'Séance terminée.'
            : '';
  const lastToast = toasts.length > 0 ? toasts[toasts.length - 1] : null;
  const sheetOnly = display === 'sheet';

  return (
    <main className="practice">
      {rewards.length > 0 && <RewardToast awards={rewards} onDone={() => setRewards([])} />}
      <header className="practice-bar">
        <a className="btn ghost back" href={href('catalogue')} aria-label="Retour au catalogue">
          ← Catalogue
        </a>
        <div className="practice-title">
          <h1>{score.title}</h1>
          <span className="muted">
            {score.timeSignature.beats}/{score.timeSignature.beatUnit} · {measures} mesures · cible ♩ = {score.tempo}
          </span>
        </div>
        <div className="seg" role="group" aria-label="Affichage">
          {(['falling', 'sheet', 'both'] as DisplayMode[]).map((d) => (
            <button key={d} type="button" className={display === d ? 'seg-btn on' : 'seg-btn'} onClick={() => changeDisplay(d)} title={`Affichage : ${DISPLAY_LABELS[d].toLowerCase()}`}>
              {DISPLAY_LABELS[d]}
            </button>
          ))}
        </div>
        <div className="seg" role="group" aria-label="Mode">
          <button type="button" className={mode === 'wait' ? 'seg-btn on' : 'seg-btn'} onClick={() => changeMode('wait')}>
            Attente
          </button>
          <button type="button" className={mode === 'tempo' ? 'seg-btn on' : 'seg-btn'} onClick={() => changeMode('tempo')}>
            Tempo imposé
          </button>
        </div>
        {handOptions.length > 0 && (
          <div className="seg" role="group" aria-label="Mains">
            {handOptions.map((o) => (
              <button key={o.short} type="button" className={sameHands(hands, o.value) ? 'seg-btn on' : 'seg-btn'} onClick={() => changeHands(o.value)} title={o.label}>
                {o.short}
              </button>
            ))}
          </div>
        )}
        <div className="tempo-control" role="group" aria-label="Tempo">
          <button type="button" className="btn small" onClick={() => changeTempo(tempo - 5)} aria-label="Ralentir">
            −
          </button>
          <label>
            <span className="tempo-value">♩ = {tempo}</span>
            <input type="range" min={score.minTempo} max={Math.round(score.tempo * 1.2)} step={1} value={tempo} onChange={(e) => changeTempo(Number(e.target.value))} aria-label="Tempo" />
          </label>
          <button type="button" className="btn small" onClick={() => changeTempo(tempo + 5)} aria-label="Accélérer">
            +
          </button>
        </div>
        <div className={`beat-indicator ${beat?.countIn ? 'count-in' : ''}`} aria-label="Métronome visuel">
          {Array.from({ length: beatsPerMeasure }, (_, i) => (
            <span key={i} className={beat && beat.beat === i + 1 && running ? 'dot on' : 'dot'} />
          ))}
        </div>
        {running ? (
          <button type="button" className="btn danger" onClick={stop}>
            Arrêter
          </button>
        ) : (
          <button type="button" className="btn primary" onClick={() => void start()}>
            {status === 'ended' ? 'Rejouer' : 'Démarrer'}
          </button>
        )}
      </header>

      <div className="practice-input">
        <button type="button" className="link" onClick={() => setShowPicker((v) => !v)} aria-expanded={showPicker}>
          Entrée : {inputLabel}
          {activeKind && inputStatus.state === 'active' ? ' · active' : ''}
          {inputStatus.state === 'lost' ? ' · connexion perdue' : ''}
          {inputStatus.state === 'error' ? ' · erreur' : ''}
          {' '}
          <span className="muted">{showPicker ? '(masquer)' : '(changer)'}</span>
        </button>
        {activeKind === 'keyboard' && <span className="muted kbd-hint">Rangée du milieu du clavier = do ré mi fa sol la si, ou cliquez sur les touches.</span>}
        {handsNotice && <span className="warn-text">{handsNotice}</span>}
        {oneHandNotice && <span className="muted">{oneHandNotice}</span>}
        {inputError && <span className="warn-text">{inputError}</span>}
        {settings.developer && (
          <span className="dev-inline">
            <button type="button" className="btn small" onClick={() => inputManager.keyboard.simulateConnection('lost')}>
              Simuler une perte
            </button>
            <button type="button" className="btn small" onClick={() => inputManager.keyboard.simulateConnection('restored')}>
              Simuler le retour
            </button>
          </span>
        )}
        {showPicker && <InputPicker />}
      </div>

      <div className={`stage-area display-${display}`}>
        {display !== 'falling' && (
          <div className="sheet-pane">
            <SheetView
              ref={sheetRef}
              score={score}
              hands={hands}
              showFingers={settings.showFingers}
              showNames={settings.showNames}
              naming={settings.naming}
              noteStates={sheetStates}
              expectedIds={expectedIds}
              initialTick={sessionRef.current ? sessionRef.current.tickAt(performance.now()) : 0}
            />
          </div>
        )}
        {sheetOnly && (
          <div className="stage-messages" aria-live="polite">
            <span className="msg-instruction">{instruction}</span>
            {lastToast && (
              <span className="msg-toast" style={{ color: lastToast.color }}>
                {lastToast.text}
              </span>
            )}
          </div>
        )}
        <div className="stage-wrap" ref={wrapRef}>
          <canvas ref={canvasRef} className="stage" onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} aria-label={display === 'sheet' ? 'Clavier' : 'Notes descendantes et clavier'} />
        </div>
        <div className="toasts" aria-live="polite" hidden={sheetOnly}>
          {toasts.map((t) => (
            <div key={t.id} className="toast" style={{ borderColor: t.color, color: t.color }}>
              {t.text}
            </div>
          ))}
        </div>
        {running && (
          <div className="stage-counter">
            <span>
              {counts.hit} / {counts.total} notes
            </span>
            {currentMeasure > 0 && (
              <span>
                mesure {currentMeasure} / {measures}
              </span>
            )}
          </div>
        )}
        {status === 'leadIn' && mode === 'tempo' && beat?.countIn && <div className="count-in-overlay">{beat.beat}</div>}
        {!sheetOnly && status === 'leadIn' && mode === 'wait' && <div className="stage-hint">Jouez chaque note quand elle atteint la ligne. La lecture attend votre note.</div>}
        {!sheetOnly && status === 'waiting' && hint && <div className="stage-hint">{hint}</div>}
        {status === 'paused' && (
          <div className="pause-overlay">
            <strong>Session en pause : {pauseReason}.</strong>
            <span>La lecture reprend automatiquement au retour de la connexion.</span>
            <button type="button" className="btn" onClick={stop}>
              Arrêter la session
            </button>
          </div>
        )}
        {!sheetOnly && status === 'idle' && !summary && (
          <div className="stage-hint idle">
            {mode === 'wait' ? 'Mode attente : la lecture attend chacune de vos notes.' : `Mode tempo imposé : décompte d’une mesure, puis ça défile à ♩ = ${tempo}.`}
          </div>
        )}
      </div>

      {summary && (
        <SummaryPanel
          summary={summary}
          score={score}
          awards={sessionAwards}
          onReplay={() => void start()}
          onSwitchToTempo={() => {
            setMode('tempo');
          }}
        />
      )}
    </main>
  );
}
